import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, Users, FileText, Globe, CreditCard, 
  BarChart3, Kanban, X, LogOut, Crown, User as UserIcon,
  Wallet, UserCog, FolderKanban, PieChart, Receipt, ShieldCheck, Star,
  ChevronDown, Settings, Building2, Bell, MessageSquare, Briefcase, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

// New Menu structure organized as requested
const menuStructure = [
  {
    category: 'Essenciais',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', id: 'dashboard' },
    ]
  },
  {
    category: 'CRM & Vendas',
    items: [
      { icon: Kanban, label: 'Leads & Funil', path: '/crm', id: 'crm' },
      { icon: BarChart3, label: 'Performance', path: '/crm/performance', id: 'crm-performance' },
    ]
  },
  {
    category: 'Carteira de Clientes',
    items: [
      { icon: Users, label: 'Clientes', path: '/clientes', id: 'clientes' },
      { icon: FileText, label: 'Contratos', path: '/contratos', id: 'contratos' },
    ]
  },
  {
    category: 'Produção',
    items: [
      { icon: FolderKanban, label: 'Projetos', path: '/projetos', id: 'projetos' },
      { icon: Globe, label: 'Hospedagem', path: '/hospedagem', id: 'hospedagem' },
    ]
  },
  {
    category: 'Financeiro',
    items: [
      { icon: CreditCard, label: 'Vendas & Faturas', path: '/financeiro', id: 'financeiro' },
      { icon: Wallet, label: 'Fluxo de Caixa', path: '/fluxo-caixa', id: 'fluxo-caixa' },
      { icon: Receipt, label: 'Custos Fixos', path: '/custos-fixos', id: 'custos-fixos' },
      { icon: BarChart3, label: 'Relatórios', path: '/relatorios', id: 'relatorios' },
      { icon: PieChart, label: 'Métricas', path: '/metricas', id: 'metricas' },
      { icon: Target, label: 'ROI & Investimentos', path: '/roi', id: 'roi' },
    ]
  },
  {
    category: 'Equipe & Gestão',
    items: [
      { icon: UserCog, label: 'Funcionários', path: '/funcionarios', id: 'funcionarios' },
      { icon: ShieldCheck, label: 'Permissões', path: '/equipe', id: 'equipe' },
    ]
  },
  {
    category: 'Sistema',
    items: [
      { icon: UserIcon, label: 'Meu Perfil', path: '/perfil', id: 'perfil' },
      { icon: Star, label: 'Assinaturas', path: '/assinaturas', id: 'assinaturas' },
      { icon: Crown, label: 'Master Panel', path: '/master', id: 'master', adminOnly: true },
    ]
  }
];

function CategoryGroup({ group, allowedIds, location, onNavClick }) {
  const visibleItems = group.items.filter(item => allowedIds.includes(item.id));
  if (visibleItems.length === 0) return null;

  return (
    <div className="mb-6 last:mb-2">
      <h3 className="px-4 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] select-none">
        {group.category}
      </h3>
      <div className="space-y-1">
        {visibleItems.map(item => (
          <NavItem key={item.path} item={item} location={location} onClick={onNavClick} />
        ))}
      </div>
    </div>
  );
}

function NavItem({ item, location, onClick }) {
  const isActive = location.pathname === item.path || 
    (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
    
  return (
    <Link
      to={item.path}
      className={cn(
        "group relative flex items-center gap-3 px-4 py-2 mx-2 rounded-xl text-sm font-medium transition-all duration-200",
        isActive
          ? "bg-[#F9F9F9] text-slate-900 shadow-sm border border-slate-100"
          : "text-slate-500 hover:bg-[#F9F9F9]/60 hover:text-slate-900"
      )}
      onClick={onClick}
    >
      <item.icon className={cn(
        "h-4 w-4 transition-transform duration-200", 
        isActive ? "text-indigo-600 scale-110" : "group-hover:scale-110"
      )} />
      <span className={cn("truncate", isActive ? "font-bold" : "font-medium")}>{item.label}</span>
      
      {/* Active side indicator */}
      {isActive && (
        <div className="absolute left-0 w-1 h-4 bg-indigo-600 rounded-r-full" />
      )}
    </Link>
  );
}

export default function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation();
  const { user, company, logout } = useAuth();
  
  const getAllowedIds = () => {
    let allowedIds = ['perfil'];
    const plan = company?.plan || 'basico';
    const role = user?.role || 'operador';

    let isTrialActive = false;
    if (company?.trial_ends_at) {
      if (new Date(company.trial_ends_at) > new Date()) {
        isTrialActive = true;
      }
    }

    let planIds = ['dashboard', 'clientes', 'contratos', 'financeiro', 'fluxo-caixa', 'custos-fixos', 'assinaturas', 'perfil'];
    
    if (plan === 'intermediario' || plan === 'full' || isTrialActive) {
      planIds.push('crm', 'crm-performance', 'projetos');
    }
    if (plan === 'full') {
      planIds.push('hospedagem', 'funcionarios', 'metricas', 'roi', 'relatorios', 'equipe');
    }

    if (user?.email?.toLowerCase()?.trim() === 'adrian@kodestudio.com.br' || 
        company?.name?.toLowerCase() === 'adrian' || 
        role === 'super_admin' || 
        role === 'admin') {
      planIds.push('master');
    }

    if (user?.email?.toLowerCase()?.trim() === 'adrian@kodestudio.com.br' || 
        company?.name?.toLowerCase() === 'adrian') {
      allowedIds = ['dashboard', 'clientes', 'contratos', 'financeiro', 'fluxo-caixa', 'custos-fixos', 'assinaturas', 'perfil', 'crm', 'crm-performance', 'projetos', 'hospedagem', 'funcionarios', 'metricas', 'roi', 'relatorios', 'equipe', 'master'];
    } else if (role === 'vendedor') {
      allowedIds = ['crm', 'crm-performance', 'perfil'];
    } else if (role === 'desenvolvedor') {
      allowedIds = ['projetos', 'perfil'];
    } else if (role === 'admin' || role === 'super_admin') {
      allowedIds = planIds;
    } else {
      allowedIds = planIds.filter(id => !['assinaturas', 'equipe', 'master'].includes(id));
    }

    return allowedIds;
  };

  const allowedIds = getAllowedIds();

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full bg-white border-r border-slate-100 transition-all duration-300 ease-in-out lg:translate-x-0 overflow-hidden",
          isOpen ? "translate-x-0" : "-translate-x-full",
          "w-[260px] shadow-2xl lg:shadow-none"
        )}
      >
        <div className="flex flex-col h-full bg-white/80 backdrop-blur-xl">
          {/* Logo Area */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-200">
                  <Building2 className="h-5 w-5 text-indigo-400" />
                </div>
                <div>
                  <h1 className="text-sm font-bold text-slate-900 tracking-tight">Kode Studio</h1>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-pulse" />
                    <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">
                      {{ basico: 'Plano Básico', intermediario: 'Intermediário', full: 'Plano Full' }[company?.plan] || company?.plan || 'SaaS'}
                    </p>
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden h-8 w-8 text-slate-400 hover:text-slate-900"
                onClick={() => setIsOpen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Navigation with Premium Scrollbar Visibility */}
          <nav className="flex-1 px-2 py-2 overflow-y-auto premium-scrollbar">
            {menuStructure.map((group, idx) => (
              <CategoryGroup
                key={group.category || `top-${idx}`}
                group={group}
                allowedIds={allowedIds}
                location={location}
                onNavClick={() => setIsOpen(false)}
              />
            ))}
          </nav>

          {/* Bottom Section: Support & Profile */}
          <div className="p-4 border-t border-slate-50 space-y-4">
            {/* Trial Info */}
            {(() => {
              if (!company?.trial_ends_at) return null;
              const trialEnds = new Date(company.trial_ends_at);
              const now = new Date();
              if (trialEnds <= now) return null;
              const daysDiff = Math.ceil((trialEnds - now) / (1000 * 60 * 60 * 24));
              
              return (
                <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-2xl text-center group transition-colors hover:bg-indigo-50">
                  <p className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest mb-1">Trial Full</p>
                  <p className="text-xl font-black text-indigo-600 leading-none">{daysDiff} dias</p>
                </div>
              );
            })()}

            {/* WhatsApp Support Card */}
            <a
              href="https://wa.me/5554992982632"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-emerald-50/40 border border-emerald-100 rounded-2xl hover:bg-emerald-50 transition-all group"
            >
              <div className="h-10 w-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white shadow-lg shadow-emerald-100 group-hover:scale-105 transition-transform">
                <MessageSquare className="h-5 w-5" />
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-bold text-emerald-800">Suporte</span>
                <span className="text-[10px] text-emerald-600 font-medium">WhatsApp Online</span>
              </div>
            </a>

            {/* Profile & Logout */}
            <div className="p-1.5 bg-slate-50 rounded-2xl flex items-center justify-between border border-slate-100/50">
              <Link 
                to="/perfil" 
                className="flex items-center gap-3 p-1 rounded-xl hover:bg-white hover:shadow-sm transition-all flex-1 mr-2"
                onClick={() => setIsOpen(false)}
              >
                <div className="h-9 w-9 rounded-xl bg-slate-900 flex items-center justify-center text-indigo-400 font-bold text-sm shadow-sm border border-slate-800">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="max-w-[100px]">
                  <p className="text-xs font-bold text-slate-900 truncate">{user?.name}</p>
                  <p className="text-[10px] text-slate-400 truncate tracking-tight">{user?.email}</p>
                </div>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
                onClick={logout}
                title="Sair"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}